import { useState, useContext, useEffect, useRef, useCallback } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { isEmpty } from 'lodash';
import _ from 'underscore';

import { getDate, getTime } from './../../utils/date';
import { getIcon } from './../../utils/fileTypes';
import { addModel, removeModel, startAnalysis } from './../../utils/forge';
import {
    IcAdd,
    IcAddFill,
    IcEye,
    IcEyeFill,
    IcObjectDate,
    IcObjectName,
    IcObjectVersion,
    IcRemove,
    IcRemoveFill,
    IcDelete,
    IcDeleteFill,
    IcDownload,
    IcLoading,
    IcRadar,
} from './../../share/icons';
import { mainContext } from './../context/mainContext';
import locale from '../../utils/localization.json';
import ObjectCardItem from './ObjectCardItem';
import {
    getAllPointClouds,
    handlePointCloudDelete,
    updatePointCloudStatus,
} from './../../actions/pointcloud';
import { cancelAnalysis } from '../../services/pointcloudService';
import { updateNotifications } from '../../actions/viewer';
import Modal from './../../share/components/base/Modal';
import ObjectCardItemToggle from './ObjectCardItemToggle';
import AnalyzeConfigModal from './AnalyzeConfigModal';
import { calculateColor } from '../../utils/colors';

const ObjectCard = ({ object, objectType }) => {
    const { urn, filename, name, type, modelId, status, date, version, url } =
        object;

    const lang = useSelector((state) => state.language);
    const viewer = useSelector((state) => state.viewer);
    const {
        permissions: {
            point_cloud: { analyze, analyze_edit },
        },
    } = useSelector((state) => state.currentProject);
    const currentObject = useSelector((state) => state.currentObject);
    const currentPanorama = useSelector((state) => state.currentPanorama);

    const {
        loadModel,
        handlePointClouds,
        handlePanoramas,
        currentLinks,
        setCurrentLinks,
        loadedPointClouds,
        removePointCloud,
        modelColored,
        setModelColored,
    } = useContext(mainContext);

    const [waiting, setWaiting] = useState(false);
    const [, forceUpdate] = useState();
    const [loading, setLoading] = useState(false);
    const [min, setMin] = useState(1);
    const [max, setMax] = useState(20);
    const [filter, setFilter] = useState({
        type: 0,
        value: { min: '', max: '', down: '', up: '' },
    });
    const [hidden, setHidden] = useState(false);
    const [shown, setShown] = useState(false);
    const [showDistance, setShowDistance] = useState(
        loadedPointClouds?.find((p) => p.filename === filename)?.showDistance ||
            false
    );
    const [showConfig, setShowConfig] = useState(false);
    const [ranges, setRanges] = useState([{ min: 0, max: 20, title: '0-20' }]);
    const [percentages, setPercentages] = useState({});

    const dispatch = useDispatch();

    const ref = useRef(null);
    const modalRef = useRef(null);

    const handleElementsTheming = useCallback(() => {
        // If the model is already colored, clear the theming.
        if (modelColored === `${name}.${type}`) {
            viewer?.clearThemingColors();
            setModelColored('');
            return;
        }

        // Ensure viewer and PointCloudExtension are available
        if (!viewer) {
            console.warn('Viewer is not available.');
            return;
        }

        const pointCloudExtension = viewer.getExtension('PointCloudExtension');
        if (!pointCloudExtension) {
            console.warn('PointCloudExtension not found on viewer.');
            return;
        }

        const pointsData = pointCloudExtension[`POINTCLOUD_${filename}`];
        if (!pointsData) {
            console.warn(`No point cloud data found for: ${filename}`);
            return;
        }

        const relevantPoints = pointsData.filter(({ E }) => E !== '');

        // Group points by Dbid and find the closest point for each Dbid
        const groupedByDbid = relevantPoints.reduce((acc, point) => {
            acc[point.E] = acc[point.E] || [];
            acc[point.E].push(point);
            return acc;
        }, {});

        const closestPoints = Object.values(groupedByDbid)
            .map((group) => {
                let minPoint = null;
                let minDistance = Infinity;

                group.forEach((point) => {
                    const currentDistance = parseInt(point.D, 10);
                    if (currentDistance < minDistance) {
                        minDistance = currentDistance;
                        minPoint = point;
                    }
                });
                return minPoint;
            })
            .filter(Boolean); // Filter out any nulls if a group was empty (shouldn't happen with current logic but good for safety)

        // Apply theming colors
        const themedDbIds = new Set();
        closestPoints.forEach(({ E, D }) => {
            const parsedD = parseInt(D, 10);
            let value;

            if (parsedD < min * 10) {
                value = 0;
            } else if (parsedD > max * 10) {
                value = 1;
            } else {
                value = (parsedD - min * 10) / (max * 10 - min * 10);
            }

            const color = calculateColor(value);
            viewer.setThemingColor(parseInt(E, 10), color);
            themedDbIds.add(parseInt(E, 10));
        });

        // Reset colors for elements not part of the current theming
        const instanceTree = viewer.model?.getData()?.instanceTree;
        if (instanceTree) {
            const allDbIds = Object.keys(
                instanceTree.nodeAccess.dbIdToIndex
            ).map(Number);
            allDbIds.forEach((dbId) => {
                if (!themedDbIds.has(dbId)) {
                    viewer.setThemingColor(
                        dbId,
                        new window.THREE.Vector4(1, 1, 1, 1) // White
                    );
                }
            });
        }

        setModelColored(`${name}.${type}`);
    }, [filename, max, min, modelColored, name, setModelColored, type, viewer]);

    const handleAddRemove = useCallback(async () => {
        try {
            if (objectType === 'model') {
                setWaiting(true);
                const linkIndex = currentLinks.findIndex(
                    (link) => link.urn === urn
                );

                if (linkIndex >= 0) {
                    removeModel(currentLinks[linkIndex].model, viewer);
                    const filteredLinks = currentLinks.filter(
                        (link) => link.urn !== urn
                    );

                    setCurrentLinks([...filteredLinks]);
                } else {
                    const model = await addModel(urn, viewer);

                    setCurrentLinks([...currentLinks, { urn, model }]);
                }
            } else if (objectType === 'pointcloud') {
                await handlePointClouds(
                    filename,
                    name,
                    showDistance,
                    min,
                    max,
                    filter,
                    url
                );
            } else {
                if (currentPanorama.name === name) await handlePanoramas();
                else await handlePanoramas(object);
            }
        } catch {
        } finally {
            setWaiting(false);
        }
    }, [
        currentLinks,
        filename,
        filter,
        handlePointClouds,
        handlePanoramas,
        objectType,
        max,
        min,
        name,
        setCurrentLinks,
        showDistance,
        url,
        urn,
        viewer,
        object,
        currentPanorama,
    ]);

    useEffect(() => {
        if (
            loadedPointClouds?.find((p) => p.filename === filename) &&
            loadedPointClouds?.find((p) => p.filename === filename).isLoaded ===
                undefined
        )
            setLoading(true);
        else setLoading(false);

        if (
            loadedPointClouds?.find((p) => p.filename === filename)
                ?.isLoaded !== undefined
        )
            setHidden(true);
        else setHidden(false);

        if (loadedPointClouds?.find((p) => p.filename === filename)?.isLoaded)
            setShown(true);
        else setShown(false);

        return () => forceUpdate(true);
    }, [loadedPointClouds, filename]);

    useEffect(() => {
        if (
            shown &&
            loadedPointClouds?.find((p) => p.filename === filename)
                ?.isLoaded !== undefined
        ) {
            ref.current.click();
            setTimeout(() => ref.current.click(), 100);
        }

        if (showDistance) {
            const extension = viewer?.getExtension('PointCloudExtension');
            if (extension) {
                const points = extension['POINTCLOUD_' + filename];
                if (points)
                    setPercentages(
                        _.countBy(points, ({ D }) => {
                            const theRange = ranges.find(
                                (r) => D > r.min * 10 && D <= r.max * 10
                            );
                            return theRange
                                ? theRange.title
                                : D > 200
                                ? '>20'
                                : '';
                        })
                    );
            }
        } else {
            Object.keys(percentages).forEach((p) =>
                dispatch(
                    updateNotifications(
                        'percentages_' + name + '_' + type + '_' + p
                    )
                )
            );
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [showDistance]);

    useEffect(() => {
        const extension = viewer?.getExtension('PointCloudExtension');
        if (extension) {
            const points = extension['POINTCLOUD_' + filename];
            if (points)
                Object.keys(percentages)
                    .sort((a, b) => (a > b ? 1 : b > a ? -1 : 0))
                    .forEach(
                        (p) =>
                            p &&
                            dispatch(
                                updateNotifications(
                                    'percentages_' +
                                        name +
                                        '_' +
                                        type +
                                        '_' +
                                        p,
                                    `${name} : ${p} cm : ${Math.round(
                                        (percentages[p] / points.length) * 100
                                    )}%`
                                )
                            )
                    );
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [percentages]);

    return (
        <div
            className={`w-full pr-4 pl-2 py-4 ${
                loading ? 'pb-3' : ''
            } flex flex-col bg-modelic-gray-800 rounded border border-modelic-gray-700`}
        >
            <div className="flex justify-between">
                <div className="max-w-[calc(100%-57px)] flex-col inline-flex gap-2">
                    <ObjectCardItem
                        icon={IcObjectName}
                        title={locale[lang].name}
                        component={
                            <div className="truncate text-modelic-gray-100 text-xs font-medium font-poppins leading-3">
                                {name}.{type}
                            </div>
                        }
                    />
                    <ObjectCardItem
                        icon={IcObjectDate}
                        title={locale[lang].date}
                        component={
                            <div className="text-modelic-gray-100 text-xs font-medium font-poppins leading-3">
                                {getDate(date)} - {getTime(date)}
                            </div>
                        }
                    />
                </div>
                <div>{getIcon(type)}</div>
            </div>
            <div
                className={`flex flex-wrap ${
                    objectType === 'model' || objectType === 'panorama'
                        ? 'mt-[7px]'
                        : analyze || !loading
                        ? 'mt-2'
                        : ''
                }`}
            >
                {objectType === 'model' ? (
                    <ObjectCardItem
                        icon={IcObjectVersion}
                        title={locale[lang].version}
                        component={
                            <div className="px-2 pt-1 pb-0.5 bg-modelic-info-darker rounded-lg border border-modelic-info-main">
                                <div className="text-modelic-info-main text-xs font-normal font-poppins content-end">
                                    V{version}
                                </div>
                            </div>
                        }
                    />
                ) : objectType === 'pointcloud' &&
                  analyze &&
                  ((!status && analyze_edit) || status) ? (
                    <div className="flex flex-col gap-2">
                        <ObjectCardItem
                            icon={IcRadar}
                            title={
                                status !== 'analyzed'
                                    ? locale[lang].analysis
                                    : locale[lang].point
                            }
                            component={
                                status === 'inprogress' ? (
                                    <>
                                        <div className="text-modelic-gray-500 text-xs font-medium font-poppins leading-3">
                                            {locale[lang].inProgress}
                                        </div>
                                        {analyze_edit && (
                                            <button
                                                className="text-modelic-gray-500 text-xs font-medium font-poppins leading-3 hover:scale-105 hover:text-modelic-warning-light"
                                                onClick={async () => {
                                                    try {
                                                        await cancelAnalysis(
                                                            {
                                                                name: `${name}_${type}.txt`,
                                                            },
                                                            currentObject._id +
                                                                currentObject.version
                                                        );
                                                    } catch {
                                                        // ignore
                                                    } finally {
                                                        dispatch(
                                                            getAllPointClouds()
                                                        );
                                                    }
                                                }}
                                            >
                                                ({locale[lang].cancel})
                                            </button>
                                        )}
                                    </>
                                ) : status === 'analyzed' ? (
                                    <>
                                        <ObjectCardItemToggle
                                            isDisabled={!hidden}
                                            isChecked={showDistance}
                                            hidden={hidden}
                                            handleToggleChange={(e) => {
                                                if (e.target.checked)
                                                    setShowConfig(true);
                                                else setShowDistance(false);
                                            }}
                                        />

                                        {analyze_edit && (
                                            <Modal
                                                lable={
                                                    <div className="text-modelic-gray-500 text-xs font-medium font-poppins leading-3 hover:scale-105 hover:text-modelic-error-dark">
                                                        ({locale[lang].clear})
                                                    </div>
                                                }
                                                title={
                                                    locale[lang]
                                                        .analysisClearTitle
                                                }
                                                tag="delete"
                                                callback={() => {
                                                    try {
                                                        dispatch(
                                                            handlePointCloudDelete(
                                                                name,
                                                                type,
                                                                modelId
                                                            )
                                                        );
                                                    } catch (err) {
                                                        // ignore
                                                    }
                                                }}
                                            >
                                                <div
                                                    ref={modalRef}
                                                    className="text-lg"
                                                >
                                                    {
                                                        locale[lang]
                                                            .analysisClearBody
                                                    }{' '}
                                                    "{name}"?
                                                </div>
                                            </Modal>
                                        )}
                                    </>
                                ) : status === 'waiting' ? (
                                    <div className="text-modelic-gray-500 text-xs font-medium font-poppins leading-3">
                                        {locale[lang].waiting} ...
                                    </div>
                                ) : (
                                    <button
                                        className="text-modelic-primary-light text-xs font-medium font-poppins leading-3 hover:scale-105"
                                        onClick={async () => {
                                            try {
                                                dispatch(
                                                    updatePointCloudStatus(
                                                        filename
                                                    )
                                                );
                                                await startAnalysis(
                                                    viewer,
                                                    filename,
                                                    currentObject._id +
                                                        currentObject.version
                                                );
                                            } catch (err) {
                                                // console.log(err);
                                            } finally {
                                                dispatch(getAllPointClouds());
                                            }
                                        }}
                                    >
                                        {locale[lang].start}
                                    </button>
                                )
                            }
                        />
                        {status === 'analyzed' && (
                            <ObjectCardItem
                                icon={IcRadar}
                                title={locale[lang].element}
                                component={
                                    <ObjectCardItemToggle
                                        isDisabled={!hidden && !modelColored}
                                        isChecked={
                                            modelColored === `${name}.${type}`
                                        }
                                        hidden={hidden}
                                        handleToggleChange={
                                            handleElementsTheming
                                        }
                                    />
                                }
                            />
                        )}
                    </div>
                ) : null}

                <div
                    className={`flex ${
                        loading
                            ? 'justify-between w-full'
                            : 'justify-end mt-[3px]'
                    } self-end grow`}
                >
                    {loading && (
                        <div className="items-center flex">
                            <div className="text-modelic-primary-lighter scale-90 mr-1.5">
                                {IcDownload}
                            </div>
                            <div className="text-modelic-primary-lighter text-xs font-normal font-poppins leading-3 mr-1.5">
                                {locale[lang].progress}:
                            </div>
                            <div className="text-modelic-gray-100 text-xs font-medium font-poppins leading-3">
                                {loadedPointClouds?.find(
                                    (p) => p.filename === filename
                                )?.progress || 0}{' '}
                                %
                            </div>
                            <div className="scale-50 text-modelic-primary-main">
                                <div className="animate-spin">{IcLoading}</div>
                            </div>
                        </div>
                    )}
                    <div className="flex gap-4">
                        {objectType === 'model' && (
                            <button
                                className={`text-modelic-primary-lighter ${
                                    !waiting ? 'group hover:scale-105' : ''
                                }`}
                                disabled={objectType === 'model' && waiting}
                                onClick={async () => {
                                    if (objectType === 'model')
                                        setWaiting(true);

                                    try {
                                        if (!isEmpty(viewer))
                                            loadModel(viewer, object, 0);
                                    } finally {
                                        setWaiting(false);
                                    }
                                }}
                            >
                                <>
                                    {IcEye}
                                    {IcEyeFill}
                                </>
                            </button>
                        )}
                        {viewer?.model?.getDocumentNode()?.data?.role ===
                            '3d' && (
                            <>
                                {hidden && (
                                    <button
                                        className="group hover:scale-105 text-modelic-error-dark"
                                        onClick={() => {
                                            setShowDistance(false);
                                            removePointCloud(filename);
                                        }}
                                    >
                                        {IcDelete}
                                        {IcDeleteFill}
                                    </button>
                                )}
                                {status !== 'inprogress' && (
                                    <button
                                        ref={ref}
                                        className={`font-poppins text-xs font-medium ${
                                            !waiting
                                                ? 'group hover:scale-105'
                                                : ''
                                        } ${
                                            currentLinks?.find(
                                                (link) => link.urn === urn
                                            ) ||
                                            shown ||
                                            currentPanorama.name === name
                                                ? 'text-modelic-error-dark'
                                                : loading
                                                ? 'text-modelic-gray-500'
                                                : 'text-modelic-primary-main'
                                        }`}
                                        disabled={
                                            objectType === 'model' && waiting
                                        }
                                        onClick={handleAddRemove}
                                    >
                                        {currentLinks?.find(
                                            (link) => link.urn === urn
                                        ) ||
                                        shown ||
                                        currentPanorama.name === name ? (
                                            <>
                                                {IcRemove}
                                                {IcRemoveFill}
                                            </>
                                        ) : loading ? (
                                            locale[lang].stop
                                        ) : (
                                            <>
                                                {IcAdd}
                                                {IcAddFill}
                                            </>
                                        )}
                                    </button>
                                )}
                            </>
                        )}
                    </div>
                </div>
            </div>

            {showConfig && (
                <AnalyzeConfigModal
                    min={min}
                    max={max}
                    filter={filter}
                    ranges={ranges}
                    setMin={setMin}
                    setMax={setMax}
                    setFilter={setFilter}
                    setRanges={setRanges}
                    setShowConfig={setShowConfig}
                    setShowDistance={setShowDistance}
                />
            )}
        </div>
    );
};

export default ObjectCard;
